import React from "react";
import Link from "next/link";
import { Footer, Header, SEO } from "@/components";
import { FinalCta } from "@/components/main";
import SEOConfig from "@/common/seo.config";
import {
  AudienceSplit,
  FaqList,
  LandingSection,
  ProcessRail,
  SectionHeader,
} from "@/components/main/landing/landing-ui";
import {
  buildBreadcrumbList,
  buildCollectionPage,
  buildFaqPage,
  buildItemListSchema,
  pageSchemas,
} from "@/lib/schema";
import { SITE } from "@/common/seo.config";

const FEATURED_SOLUTIONS = [
  {
    slug: "enterprise-ai-chatbot-development",
    title: "Enterprise AI chatbots",
    description:
      "Customer and employee-facing assistants grounded in your knowledge, with escalation paths and operational controls.",
  },
  {
    slug: "ai-knowledge-assistants-for-organizations",
    title: "Knowledge assistants",
    description:
      "Internal search and Q&A over policies, SOPs, and documentation so teams find answers without digging through folders.",
  },
  {
    slug: "ai-data-intelligence-platforms-for-businesses",
    title: "Data intelligence platforms",
    description:
      "Turn documents and operational data into structured insight your systems and people can act on.",
  },
  {
    slug: "ai-solutions-for-logistics-operations",
    title: "Logistics operations",
    description:
      "Document processing, shipment context, and operational support designed for logistics and supply chain teams.",
  },
  {
    slug: "ai-solutions-for-marina-management",
    title: "Marina and port operations",
    description:
      "Scheduling support, vessel data handling, and operational coordination for marina and port environments.",
  },
];

const CAPABILITIES = [
  {
    title: "Grounded answers",
    description:
      "Retrieval from approved sources with citations, so responses stay tied to your content.",
  },
  {
    title: "Tool and system access",
    description:
      "Agents call APIs, update records, and move work across the tools you already run.",
  },
  {
    title: "Human review where it matters",
    description:
      "Exception paths, approvals, and audit trails for decisions that need an owner.",
  },
  {
    title: "Measurable outcomes",
    description:
      "Clear success criteria: time saved, error rates, throughput, and operator adoption.",
  },
];

const ENGAGEMENT = [
  {
    title: "Discovery",
    description:
      "We define the workflow, data, constraints, and success metrics before design begins.",
  },
  {
    title: "Pilot",
    description:
      "A focused build that proves value and integration risk with real users and real data.",
  },
  {
    title: "Production",
    description:
      "Hardening, monitoring, documentation, and handoff so operations can own the system.",
  },
  {
    title: "Operate",
    description:
      "Support for iteration after launch: evaluation, model updates, and process changes.",
  },
];

const PROBLEMS = [
  {
    title: "Document-heavy workflows",
    description:
      "Intake, classification, extraction, and routing that still depend on people reading every file.",
  },
  {
    title: "Knowledge trapped in folders",
    description:
      "Policies and SOPs exist, but finding the right answer takes too long for frontline teams.",
  },
  {
    title: "Handoffs across systems",
    description:
      "Work stalls between ERP, email, spreadsheets, and chat because nothing stitches the steps.",
  },
  {
    title: "Exception handling at scale",
    description:
      "The happy path is automated; the exceptions still burn hours for senior operators.",
  },
];

const FAQS = [
  {
    question: "Do solutions come ready to install?",
    answer:
      "They are starting points. We adapt architecture, data sources, and review rules to your systems and operating model.",
  },
  {
    question: "What if my industry is not listed?",
    answer:
      "Bring the workflow anyway. Many engagements start as a custom service build when the domain is specific.",
  },
];

function SolutionsIndexPage({ solutions = [] }) {
  const catalog =
    solutions.length > 0
      ? solutions.map((s) => ({
          slug: s.slug,
          title: s.title || s.name,
          description:
            s.short_description ||
            s.description ||
            "Explore how this solution applies to your operations.",
        }))
      : FEATURED_SOLUTIONS;

  const structuredData = pageSchemas([
    buildCollectionPage({
      path: "/solutions",
      name: SEOConfig.solutions.pageTitle,
      description: SEOConfig.solutions.pageDescription,
    }),
    buildItemListSchema({
      name: "Nivalabs AI solutions",
      path: "/solutions",
      items: catalog.map((item) => ({
        name: item.title,
        path: `/solutions/${item.slug}`,
      })),
    }),
    buildFaqPage(FAQS, { id: `${SITE.siteUrl}/solutions#faq` }),
    buildBreadcrumbList([
      { name: "Home", path: "/" },
      { name: "Solutions", path: "/solutions" },
    ]),
  ]);

  return (
    <>
      <SEO {...SEOConfig.solutions} structuredData={structuredData} />
      <Header />
      <main>

        <section className="relative overflow-hidden">
          <div className="landing-hero-glow" aria-hidden />
          <div className="landing-hero-grid" aria-hidden />
          <div className="container relative z-10 max-w-7xl py-14 md:py-16 lg:py-20">
            <div className="max-w-4xl text-left">
              <h1 className="text-3xl font-bold tracking-tight text-ink sm:text-4xl md:text-5xl lg:text-[3.5rem]">
                AI for the work
                <span className="text-main"> your teams already do</span>
              </h1>
              <p className="mt-5 max-w-3xl text-[1.0625rem] leading-relaxed text-ink-muted md:text-lg">
                Industry and operational solutions designed around documents,
                systems, and handoffs. Browse what we build, or bring us a
                problem that does not fit a template.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link href="/contact" className="landing-btn-primary">
                  Discuss a solution
                </Link>
                <Link href="/use-cases" className="landing-btn-secondary">
                  Browse use cases
                </Link>
              </div>
            </div>
          </div>
        </section>

        <LandingSection>
          <SectionHeader
            title="Problems these solutions address"
            lead="If the pain lives in documents, knowledge, or cross-system handoffs, you are in the right place."
          />
          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            {PROBLEMS.map((item) => (
              <div key={item.title} className="border-t border-line pt-5">
                <h3 className="text-lg font-semibold text-ink">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-muted">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </LandingSection>

        <LandingSection muted>
          <SectionHeader
            title="Featured solutions"
            lead="Starting points we adapt to your systems, data, and operating model."
          />
          <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {catalog.map((item) => (
              <Link
                key={item.slug}
                href={`/solutions/${item.slug}`}
                className="group flex h-full flex-col rounded-2xl border border-line bg-surface p-6 no-underline transition-colors duration-200 hover:border-[#5aada4]/55"
              >
                <h3 className="text-xl font-semibold text-ink transition-colors group-hover:text-[#1c8c80]">
                  {item.title}
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-muted">
                  {item.description}
                </p>
                <span className="mt-5 text-sm font-semibold text-ink">
                  View details →
                </span>
              </Link>
            ))}
          </div>
        </LandingSection>

        <LandingSection>
          <SectionHeader
            title="What every solution includes"
            lead="Shared foundations so delivery stays consistent across domains."
          />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {CAPABILITIES.map((item) => (
              <div key={item.title} className="border-t border-line pt-5">
                <h3 className="text-base font-semibold text-ink">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-muted">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </LandingSection>

        <LandingSection muted>
          <SectionHeader
            title="How engagements run"
            lead="A simple structure from first conversation to a system in production."
          />
          <ProcessRail steps={ENGAGEMENT} />
        </LandingSection>

        <LandingSection>
          <SectionHeader
            title="When a catalog solution fits"
            lead="Use the catalog when the problem matches. Use a service engagement when the workflow is unique."
          />
          <AudienceSplit
            forItems={[
              "Your problem maps closely to an existing solution pattern",
              "You want a faster path from discovery to pilot",
              "Domain constraints are well understood",
            ]}
            notForItems={[
              "The workflow is highly custom and needs greenfield design",
              "You primarily need platform consulting with no build",
              "Success criteria are still undefined",
            ]}
          />
        </LandingSection>

        <LandingSection muted>
          <SectionHeader
            title="Prefer a custom build?"
            lead="Many clients start from a service engagement when the problem is specific."
          />
          <div className="mt-8 flex flex-wrap gap-3">
            <Link href="/ai-agent-development" className="landing-btn-secondary">
              AI agents
            </Link>
            <Link
              href="/custom-llm-application-development"
              className="landing-btn-secondary"
            >
              LLM applications
            </Link>
            <Link href="/ai-workflow-automation" className="landing-btn-secondary">
              Workflow automation
            </Link>
            <Link href="/how-we-work" className="landing-btn-secondary">
              How we work
            </Link>
          </div>
        </LandingSection>

        <LandingSection>
          <SectionHeader title="Questions about solutions" />
          <FaqList items={FAQS} />
        </LandingSection>

        <FinalCta
          title="Have a domain we have not listed?"
          descriptionPrimary="Tell us how your operations work. We will map whether an existing solution fits or a custom build is the better path."
          linkText="Talk to an architect"
        />
      </main>
      <Footer />
    </>
  );
}

export async function getServerSideProps({ res: response }) {
  const apiBase =
    process.env.NEXT_PUBLIC_API_URL || "https://backend.pysquad.com/api/v1";

  try {
    // No limit/offset: this endpoint returns the full published set as a bare
    // array. Passing a limit would switch it to a paginated envelope.
    const res = await fetch(`${apiBase}/nivalabs/solutions/`, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      return { props: { solutions: [] } };
    }
    const data = await res.json();
    const solutions = Array.isArray(data)
      ? data
      : data.results || data.data || [];

    response.setHeader(
      "Cache-Control",
      "public, s-maxage=600, stale-while-revalidate=86400",
    );

    return { props: { solutions } };
  } catch {
    return { props: { solutions: [] } };
  }
}

export default SolutionsIndexPage;
